import { Box, ButtonBase, Icon, styled } from "@mui/material";
import useSettings from "../../hooks/useSettings";
import React, { Fragment, useState } from "react";
import { NavLink } from "react-router-dom";
import { Paragraph, Span } from "../Typography";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import { useSelector } from "react-redux";
import { isGranded } from "../../utils/utils";
const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: "12px",
  marginTop: "20px",
  marginLeft: "15px",
  marginBottom: "10px",
  textTransform: "uppercase",
  display: mode === "compact" && "none",
  color: theme.palette.text.secondary,
}));

const ExtAndIntCommon = {
  display: "flex",
  overflow: "hidden",
  borderRadius: "10px",
  height: 44,
  whiteSpace: "pre",
  marginBottom: "4px",
  marginTop: "4px",
  textDecoration: "none",
  justifyContent: "space-between",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": { 
    background: "rgba(255, 255, 255, 0.05)",
    transform: "translateX(2px)",
  },
  "&.compactNavItem": {
    overflow: "hidden",
    justifyContent: "center !important",
    borderRadius: "12px",
    margin: "4px 6px",
  },
  "& .icon": {
    fontSize: "20px",
    paddingLeft: "16px",
    paddingRight: "16px",
    verticalAlign: "middle",
    transition: "color 0.2s ease",
  },
};
const ExternalLink = styled("a")(({ theme }) => ({
  ...ExtAndIntCommon,
  color: "rgba(255, 255, 255, 0.8)",
  margin: "4px 8px",
  "&.submenu-item": {
    marginLeft: "12px",
    paddingLeft: "16px",
    fontSize: "0.8125rem",
    fontWeight: 500,
    borderRadius: "8px",
    transition: "all 0.2s ease",
    color: "rgba(255, 255, 255, 0.7) !important",
    "& .icon": {
      fontSize: "16px",
      width: 32,
      paddingLeft: "12px",
      paddingRight: "12px",
      color: "rgba(255, 255, 255, 0.6)",
    },
    "&:hover": {
      background: "rgba(255, 255, 255, 0.04)",
      color: "#ffffff !important",
      transform: "translateX(4px)",
    },
  },
}));

const InternalLink = styled(Box)(({ theme }) => ({
  "& a": {
    ...ExtAndIntCommon,
    color: "rgba(255, 255, 255, 0.8)",
    margin: "4px 8px",
    position: "relative",
  },
  "& .navItemActive": {
    background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
    color: "#ffffff !important",
    boxShadow: "0 4px 14px rgba(59, 130, 246, 0.3)",
    "& .icon": {
      color: "#ffffff !important",
    },
    "&:hover": {
      background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
      transform: "none",
    },
  },
  // Style for sub-menu items (nested inside expansion panel)
  "&.submenu-item a": {
    margin: "4px 8px 4px 16px",
    paddingLeft: "8px",
    fontSize: "0.8125rem",
    fontWeight: 500,
    borderRadius: "8px",
    transition: "all 0.2s ease",
    color: "rgba(255, 255, 255, 0.7) !important",
    "& .icon": {
      fontSize: "16px",
      width: 32,
      paddingLeft: "8px",
      paddingRight: "8px",
      color: "rgba(255, 255, 255, 0.5)",
    },
    "&:hover": {
      background: "rgba(255, 255, 255, 0.04)",
      color: "#ffffff !important",
      transform: "translateX(4px)",
    },
  },
  "&.submenu-item .navItemActive": {
    background: "rgba(255, 255, 255, 0.08) !important",
    color: "#ffffff !important",
    boxShadow: "none",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "4px",
      top: "14px",
      width: "4px",
      height: "16px",
      background: "#3b82f6",
      borderRadius: "2px",
    },
  },
}));

const StyledText = styled(Span)(({ mode }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  paddingLeft: "0.8rem",
  display: mode === "compact" && "none",
  transition: "all 0.2s ease",
}));

const BulletIcon = styled("div")(({ theme }) => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.4)",
  marginLeft: "24px",
  marginRight: "8px",
  transition: "all 0.2s ease",
}));

const BadgeValue = styled("div")(() => ({
  padding: "2px 8px",
  fontSize: "10px",
  fontWeight: 700,
  borderRadius: "10px",
  background: "#3b82f6",
  color: "#ffffff",
  marginRight: "8px",
}));

const MatxVerticalNav = ({ items }) => {
  const [state, setState] = useState({});
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;
  // Support both old and new store structure
  const permission = useSelector((state) => 
    state.permission?.permissions || state.config?.Permission || []
  );
  const isAdmin = useSelector((state) => (state.auth || state.logged || {}).isAdmin);
  const handleCollapse = (value = true, index) => {
    setState({
      [index]: value,
    });
  };
  const renderLevels = (data, level = 0) => {
    if (data.some((element) => element.permission)) {
      data.forEach((element, index) => {
        if (element.permission && !isGranded(element.permission, permission)) {
          data.splice(index, 1);
        }
      });
    }
    if (data.some((element) => element.adminOnly)&&!isAdmin) {
      data.forEach((element, index) => {
        if (element.adminOnly && element.adminOnly !== isAdmin) {
          data.splice(index, 1);
        }
      });
    }
    const isSubMenu = level > 0;
    return data.map((item, index) => {
      if (item.type === "label")
        return (
          <ListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );

      if (item.children && item.children.length > 0) {
        return (
          <MatxVerticalNavExpansionPanel
            mode={mode}
            item={item}
            key={index}
            collapsedNew={!state[index]}
            index={index}
            handleCollapse={handleCollapse}
          >
            {renderLevels(item.children, level + 1)}
          </MatxVerticalNavExpansionPanel>
        );
      } else if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            className={`${mode === "compact" && "compactNavItem"} ${isSubMenu ? "submenu-item" : ""}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ButtonBase key={item.name} name="child" sx={{ width: "100%" }}>
              {(() => {
                if (item.icon) {
                  return <Icon className="icon">{item.icon}</Icon>;
                } else {
                  return (
                    <span className="item-icon icon-text">{item.iconText}</span>
                  );
                }
              })()}
              <StyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </StyledText>
              <Box mx="auto"></Box>
              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else if (item.path) {
        return (
          <InternalLink key={index} className={isSubMenu ? "submenu-item" : ""}>
            <NavLink
              to={item.path}
              end={item.path === '/dashboard/default' || item.path === '/'}
              className={({ isActive }) =>
                isActive
                  ? `navItemActive ${mode === "compact" ? "compactNavItem" : ""}`
                  : `${mode === "compact" ? "compactNavItem" : ""}`
              }
            >
              <ButtonBase key={item.name} name="child" sx={{ width: "100%" }}>
                {item?.icon ? (
                  <Icon className="icon" sx={{ width: 36 }}>
                    {item.icon}
                  </Icon>
                ) : (
                  <Fragment>
                    <BulletIcon
                      className={`nav-bullet`}
                      sx={{ 
                        display: mode === "compact" && "none",
                        width: isSubMenu ? "3px" : "4px",
                        height: isSubMenu ? "3px" : "4px",
                        background: isSubMenu 
                          ? "#ffffff"
                          : undefined,
                      }}
                    />
                    <Box
                      className="nav-bullet-text"
                      sx={{
                        ml: "20px",
                        fontSize: "11px",
                        display: mode !== "compact" && "none",
                      }}
                    >
                      {item.iconText}
                    </Box>
                  </Fragment>
                )}
                <StyledText 
                  mode={mode} 
                  className="sidenavHoverShow"
                  sx={{
                    fontSize: isSubMenu ? "0.8125rem" : "0.875rem",
                    fontWeight: isSubMenu ? 500 : 400,
                    color: isSubMenu ? "#ffffff" : undefined,
                  }}
                >
                  {item.name}
                </StyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">
                    {item.badge.value}
                  </BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        );
      }
      return false;
    });
  };

  const handleMouseLeave = () => {
    if (mode === 'compact') {
      setState({});
    }
  };

  return (
    <div className="navigation" onMouseLeave={handleMouseLeave}>
      {renderLevels(items)}
    </div>
  );
};

export default React.memo(MatxVerticalNav);
